import { Button } from 'primereact/button';
import { useTranslation } from 'react-i18next';

import SearchBar from '../../../../components/search-bar/SearchBar';
import { useGetPermittedCompanies } from '../../../../hooks/api/company';
import { useAuth } from '../../../../hooks/auth/useAuth';
import useDialogContext from '../../../../hooks/dialog/useDialogContext';
import { useProductsTableQueryParams } from '../../../../hooks/useProductsTableQueryParams';
import CompanyFilter from '../../../../shared/components/company-filter/CompanyFilter';
import LocationFilter from '../../../../shared/components/location-filter/LocationFilter';
import { serializeMulti } from '../../../../shared/helpers/url-parameter-parser';
import { ITag } from '../../../../shared/interfaces/ITag';
import ProductActionsButton from '../product-actions-button/ProductActionsButton';
import TagsFilter from '../tags-filter/TagsFilter';
import styles from './ProductsHeader.module.css';

type ProductsHeaderProps = {
  companyTags: ITag[];
};

const ProductsHeader = ({ companyTags }: ProductsHeaderProps): JSX.Element => {
  const { t } = useTranslation();
  const { user, isBasfSuperAdmin, isBasfAdmin, isBasfNonAdmin, isBasfUser } = useAuth();
  const { openDialog } = useDialogContext();

  const { data: companies, isLoading } = useGetPermittedCompanies({
    enabled: isBasfAdmin || isBasfNonAdmin, // Only fetch companies if user is BASF Admin or Non-Admin
    select: (data) => {
      return data.filter((company) => {
        return company.id !== user?.company?.id;
      });
    },
  });

  const { filters, setParams } = useProductsTableQueryParams();

  const onSearchChange = (value: string) => {
    setParams({ search: value, page: 1 });
  };

  const onLocationsFilterChange = (countries: string[], regions: string[]) => {
    setParams({
      country: serializeMulti(countries),
      region: serializeMulti(regions),
      page: 1,
    });
  };

  const onCompanyFilterChange = (companyIds: string[], companyBpns: string[]) => {
    // Update params based on whether it's a companyId or BPN
    setParams({
      permittedCompanyId: companyIds?.length
        ? companyIds.map((id) => {
            return String(id);
          })
        : undefined,
      permittedBpn: companyBpns?.length
        ? companyBpns.map((bpn) => {
            return String(bpn);
          })
        : undefined,
      page: 1,
    });
  };

  return (
    <div className='flex flex-column md:flex-wrap md:flex-row w-full align-items-center gap-2 md:justify-content-between'>
      <div className='flex flex-column md:flex-wrap md:flex-row gap-2 w-full md:w-auto'>
        {!isBasfNonAdmin && (
          <Button
            label={t('productsPage.buttons.addProduct')}
            onClick={() => {
              openDialog('add-product-dialog');
            }}
          />
        )}
        {isBasfSuperAdmin && <ProductActionsButton />}
        {!isBasfUser && (
          <Button
            label={t('productsPage.buttons.importData')}
            onClick={() => {
              openDialog('import-product-pcf-dialog');
            }}
          />
        )}
      </div>
      <div
        className={`flex flex-column md:flex-wrap md:flex-row gap-2 md:justify-content-end align-items-center ${styles['header-filters']}`}
      >
        <label className='hidden md:block'>{t('productsPage.filterText')} </label>

        {/* Company Filter */}
        {(isBasfAdmin || isBasfNonAdmin) && (
          <div className='w-full flex-1'>
            <CompanyFilter
              companies={companies || []}
              isLoading={isLoading}
              onCompanyFilterChange={onCompanyFilterChange}
            />
          </div>
        )}

        <div className='w-full flex-1'>
          {/* Location Filter */}
          <LocationFilter onLocationsFilterChange={onLocationsFilterChange} filters={filters} />
        </div>

        {/* Tags Filter */}
        {user?.company?.license !== 'basic' && (
          <div className='w-full flex-1'>
            {' '}
            <TagsFilter companyTags={companyTags} />
          </div>
        )}
        <div className='w-full flex-1'>
          <SearchBar onSearchChange={onSearchChange} initialValue={filters.search} />
        </div>
      </div>
    </div>
  );
};

export default ProductsHeader;
